import { useState, useCallback } from 'react';
import { validateEmail, validateName, validatePhone, validateUrl, ValidationResult } from '../utils/validation';

export interface FormErrors {
  [key: string]: string;
}

export interface UseB2BFormValidationReturn {
  errors: FormErrors;
  validateField: (name: string, value: any, rules?: ValidationRule[]) => boolean;
  validateForm: (formData: any, validationRules: ValidationRules) => boolean;
  clearErrors: () => void;
  setFieldError: (field: string, error: string) => void;
  clearFieldError: (field: string) => void;
}

export type ValidationRule = 
  | 'required'
  | 'email'
  | 'phone'
  | 'url'
  | 'name'
  | { type: 'minLength'; value: number }
  | { type: 'maxLength'; value: number }
  | { type: 'custom'; validate: (value: any) => ValidationResult };

export interface ValidationRules {
  [fieldName: string]: ValidationRule[];
}

export const useB2BFormValidation = (): UseB2BFormValidationReturn => {
  const [errors, setErrors] = useState<FormErrors>({});

  const validateSingleRule = useCallback((value: any, rule: ValidationRule): ValidationResult => {
    if (rule === 'required') {
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return { isValid: false, error: 'This field is required' };
      }
      if (Array.isArray(value) && value.length === 0) {
        return { isValid: false, error: 'This field is required' };
      }
      return { isValid: true };
    }

    if (rule === 'email') {
      return validateEmail(value);
    }

    if (rule === 'phone') {
      return validatePhone(value);
    }

    if (rule === 'url') {
      return validateUrl(value);
    }

    if (rule === 'name') {
      return validateName(value);
    }

    if (typeof rule === 'object') {
      if (rule.type === 'minLength') {
        if (typeof value === 'string' && value.length < rule.value) {
          return { isValid: false, error: `Must be at least ${rule.value} characters` };
        }
        return { isValid: true };
      }

      if (rule.type === 'maxLength') {
        if (typeof value === 'string' && value.length > rule.value) {
          return { isValid: false, error: `Must be less than ${rule.value} characters` };
        }
        return { isValid: true };
      }

      if (rule.type === 'custom') {
        return rule.validate(value);
      }
    }

    return { isValid: true };
  }, []);

  const validateField = useCallback((name: string, value: any, rules: ValidationRule[] = []): boolean => {
    let fieldError = '';

    for (const rule of rules) {
      const result = validateSingleRule(value, rule);
      if (!result.isValid) {
        fieldError = result.error || 'Invalid value';
        break;
      }
    }

    setErrors(prev => ({
      ...prev,
      [name]: fieldError
    }));

    return fieldError === '';
  }, [validateSingleRule]);

  const validateForm = useCallback((formData: any, validationRules: ValidationRules): boolean => {
    const newErrors: FormErrors = {};
    let isFormValid = true;

    Object.keys(validationRules).forEach(fieldName => {
      const rules = validationRules[fieldName];
      const value = formData[fieldName];
      let fieldError = '';

      for (const rule of rules) {
        const result = validateSingleRule(value, rule);
        if (!result.isValid) {
          fieldError = result.error || 'Invalid value';
          isFormValid = false;
          break;
        }
      }

      if (fieldError) {
        newErrors[fieldName] = fieldError;
      }
    });

    setErrors(newErrors);
    return isFormValid;
  }, [validateSingleRule]);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const setFieldError = useCallback((field: string, error: string) => {
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  return {
    errors,
    validateField,
    validateForm,
    clearErrors,
    setFieldError,
    clearFieldError
  };
};
